import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { I18nProvider } from './i18n/I18nContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import './styles/index.css';

// When a new deploy lands, the browser's cached index.html still references
// the OLD chunk hashes. Navigating to a lazy route fails with
// "Failed to fetch dynamically imported module". We hard-reload once so the
// user silently picks up the new bundle instead of seeing an error screen.
function installReloadOnStaleChunk() {
  const RELOADED_FLAG = '__myday_chunk_reload__';
  function looksStale(msg: string | undefined) {
    if (!msg) return false;
    return (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('error loading dynamically imported module') ||
      msg.includes('Importing a module script failed')
    );
  }
  function reloadOnce() {
    // Avoid an infinite reload loop if the real network is broken.
    if (sessionStorage.getItem(RELOADED_FLAG)) return;
    sessionStorage.setItem(RELOADED_FLAG, '1');
    window.location.reload();
  }
  // Vite 6 emits this specifically for preloadable chunks.
  window.addEventListener('vite:preloadError', reloadOnce);
  // Catch the case where the error bubbles up as a plain rejection.
  window.addEventListener('unhandledrejection', (e) => {
    if (looksStale(e.reason?.message)) reloadOnce();
  });
  window.addEventListener('error', (e) => {
    if (looksStale(e.message) || looksStale(e.error?.message)) reloadOnce();
  });
  // Clear the flag on a successful navigation so a later real stale
  // situation can trigger a reload again.
  window.addEventListener('load', () => sessionStorage.removeItem(RELOADED_FLAG));
}
installReloadOnStaleChunk();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <I18nProvider>
          <App />
        </I18nProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>,
);
